import {
    AlertCircle,
    AlertTriangle,
    Building2,
    CheckCircle,
    ChevronDown,
    ChevronRight,
    Clock,
    Download,
    FileText,
    HelpCircle,
    Loader,
    Play,
    RefreshCw,
    Search,
    Settings,
    Users,
    XCircle
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import invoiceService, { cronFlagsService, tenantsService } from '../services/invoiceService';
import './InvoiceMonitoring.css';

// ─── helpers ────────────────────────────────────────────────────────────────

function formatDate(str) {
    if (!str) return '—';
    return new Date(str).toLocaleString('it-IT', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function formatCurrency(cents) {
    if (cents == null) return '—';
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(
        cents / 100
    );
}

function currentYearMonth() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function prevYearMonth(ym) {
    const [y, m] = ym.split('-').map(Number);
    if (m === 1) return `${y - 1}-12`;
    return `${y}-${String(m - 1).padStart(2, '0')}`;
}

// ─── status badge ─────────────────────────────────────────────────────────

const RUN_STATUS = {
    COMPLETED: { icon: CheckCircle, label: 'Completato', cls: 'run-completed' },
    FAILED: { icon: XCircle, label: 'Fallito', cls: 'run-failed' },
    RUNNING: { icon: Loader, label: 'In esecuzione', cls: 'run-running' },
};

function RunStatusBadge({ status }) {
    const cfg = RUN_STATUS[status] || { icon: Clock, label: status, cls: 'run-unknown' };
    const Icon = cfg.icon;
    return (
        <span className={`run-status-badge ${cfg.cls}`}>
            <Icon size={14} />
            {cfg.label}
        </span>
    );
}

const ACUBE_STATUS = {
    sent: { label: 'Inviata', cls: 'acube-sent' },
    delivered: { label: 'Consegnata', cls: 'acube-delivered' },
    quarantena: { label: 'In quarantena', cls: 'acube-quarantena' },
    error: { label: 'Errore', cls: 'acube-error' },
    pending: { label: 'In attesa', cls: 'acube-pending' },
};

function AcubeBadge({ status }) {
    if (!status) return <span className="acube-badge acube-pending">In attesa</span>;
    const cfg = ACUBE_STATUS[status] || { label: status, cls: 'acube-pending' };
    return <span className={`acube-badge ${cfg.cls}`}>{cfg.label}</span>;
}

// ─── CSV export helpers ─────────────────────────────────────────────────────

function escapeCsv(val) {
    if (val == null) return '';
    const s = String(val);
    if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
    return s;
}

function downloadCsv(filename, rows) {
    // UTF-8 BOM so Excel opens it correctly with accents
    const bom = '\uFEFF';
    const csv = bom + rows.map((r) => r.map(escapeCsv).join(',')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

function exportCoverageCsv(data) {
    if (!data) return;
    const header = [
        'Tenant', 'Locale/i', 'Worker', 'Email', 'CF presente',
        'Stato', 'Numero Fattura', 'Stato A-Cube',
        'Comm. fatturate (€)', 'Mance lorde (€)', 'Da fatturare (€)',
        'Trasferimenti', 'Errore A-Cube',
    ];
    const rows = [header];
    for (const tenant of data.tenants || []) {
        for (const w of tenant.workers || []) {
            const statusLabel = WORKER_STATUS_CFG[w.status]?.label || w.status;
            rows.push([
                tenant.tenantName,
                tenant.venues.join(' | '),
                w.workerName || '',
                w.workerEmail || '',
                w.hasTaxId ? 'Sì' : 'No',
                statusLabel,
                w.invoice?.invoiceNumber || '',
                w.invoice?.acubeInvoiceStatus || '',
                w.feeTotalInvoiced != null ? (w.feeTotalInvoiced / 100).toFixed(2) : '',
                w.amountGross != null ? (w.amountGross / 100).toFixed(2) : '',
                w.feeTotalPending != null ? (w.feeTotalPending / 100).toFixed(2) : '',
                w.transferCount,
                w.invoice?.acubeInvoiceError || '',
            ]);
        }
    }
    downloadCsv(`copertura-fatture-${data.yearMonth}.csv`, rows);
}

function exportRunsCsv(runs) {
    const header = [
        'Mese', 'Stato', 'Worker valutati', 'Fatture generate',
        'Fatture inviate', 'Fatture fallite', 'Saltate',
        'Avviato da', 'Data avvio', 'Data fine', 'Dry run',
    ];
    const rows = [header];
    for (const r of runs || []) {
        rows.push([
            r.yearMonth || '',
            r.status || '',
            r.totalWorkers ?? '',
            r.invoicesGenerated ?? '',
            r.invoicesSent ?? '',
            r.invoicesFailed ?? '',
            r.invoicesSkipped ?? '',
            r.triggeredBy || '',
            r.startedAt ? new Date(r.startedAt).toLocaleString('it-IT') : '',
            r.finishedAt ? new Date(r.finishedAt).toLocaleString('it-IT') : '',
            r.isDryRun ? 'Sì' : 'No',
        ]);
    }
    downloadCsv('storico-job-fatture.csv', rows);
}

function exportRetryCandidatesCsv(candidates) {
    const header = ['Percettore', 'Email', 'Fattura', 'Mese', 'Stato A-Cube', 'Motivo retry', 'Generata il'];
    const rows = [header];
    for (const inv of candidates || []) {
        const name = [inv.workerPaymentProfile?.verified_first_name, inv.workerPaymentProfile?.verified_last_name]
            .filter(Boolean).join(' ') || '—';
        rows.push([
            name,
            inv.workerPaymentProfile?.user?.email || '—',
            inv.invoiceNumber || '',
            inv.yearMonth || '',
            inv.acubeInvoiceStatus || 'pending',
            inv.reason || '',
            inv.generatedAt ? new Date(inv.generatedAt).toLocaleString('it-IT') : '',
        ]);
    }
    const timestamp = new Date().toISOString().slice(0, 16).replace('T', '_').replace(':', '-');
    downloadCsv(`retry-simulazione-${timestamp}.csv`, rows);
}

// ─── main component ──────────────────────────────────────────────────────────

// ─── SortTh — intestazione colonna ordinabile ────────────────────────────────
function SortTh({ label, sortKey, current, dir, onSort, className, style }) {
    const active = current === sortKey;
    return (
        <th
            className={className}
            style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', ...style }}
            onClick={() => onSort(sortKey)}
            title={`Ordina per ${label}`}
        >
            {label}
            <span style={{ marginLeft: 4, opacity: active ? 1 : 0.25, fontSize: 11 }}>
                {active ? (dir === 'asc' ? '▲' : '▼') : '▲'}
            </span>
        </th>
    );
}

const InvoiceMonitoring = () => {
    const navigate = useNavigate();

    // job history
    const [runs, setRuns] = useState([]);
    const [runsLoading, setRunsLoading] = useState(false);
    const [runsError, setRunsError] = useState(null);
    const [runsSort, setRunsSort] = useState({ key: 'startedAt', dir: 'desc' });

    const sortedRuns = useMemo(() => {
        const { key, dir } = runsSort;
        return [...runs].sort((a, b) => {
            let av = a[key] ?? '';
            let bv = b[key] ?? '';
            if (typeof av === 'number' && typeof bv === 'number') {
                return dir === 'asc' ? av - bv : bv - av;
            }
            // date strings
            if (key === 'startedAt') {
                av = av ? new Date(av).getTime() : 0;
                bv = bv ? new Date(bv).getTime() : 0;
                return dir === 'asc' ? av - bv : bv - av;
            }
            return dir === 'asc'
                ? String(av).localeCompare(String(bv))
                : String(bv).localeCompare(String(av));
        });
    }, [runs, runsSort]);

    function handleRunsSort(key) {
        setRunsSort((prev) =>
            prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }
        );
    }

    // month verification
    const [verifyMonth, setVerifyMonth] = useState(prevYearMonth(currentYearMonth()));
    const [verifyRun, setVerifyRun] = useState(null);
    const [verifyInvoices, setVerifyInvoices] = useState([]);
    const [verifyLoading, setVerifyLoading] = useState(false);
    const [verifyError, setVerifyError] = useState(null);
    const [expandedErrors, setExpandedErrors] = useState(false);
    const [verifySort, setVerifySort] = useState({ key: 'generatedAt', dir: 'desc' });

    const sortedVerifyInvoices = useMemo(() => {
        const { key, dir } = verifySort;
        const getValue = (inv) => {
            if (key === 'percettore') {
                const p = inv.workerPaymentProfile;
                return `${p?.verified_last_name || ''} ${p?.verified_first_name || ''}`.trim().toLowerCase();
            }
            if (key === 'generatedAt') return inv.generatedAt ? new Date(inv.generatedAt).getTime() : 0;
            if (key === 'totalFeeTotal') return inv.totalFeeTotal ?? 0;
            return String(inv[key] ?? '').toLowerCase();
        };
        return [...verifyInvoices].sort((a, b) => {
            const av = getValue(a);
            const bv = getValue(b);
            if (typeof av === 'number') return dir === 'asc' ? av - bv : bv - av;
            return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
        });
    }, [verifyInvoices, verifySort]);

    function handleVerifySort(key) {
        setVerifySort((prev) =>
            prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }
        );
    }

    // manual trigger — monthly job
    const [triggerMonth, setTriggerMonth] = useState(prevYearMonth(currentYearMonth()));
    const [coverageKey, setCoverageKey] = useState(0);
    const [triggerDryRun, setTriggerDryRun] = useState(false);
    const [triggerTenantId, setTriggerTenantId] = useState('');
    const [triggerLoading, setTriggerLoading] = useState(false);
    const [triggerResult, setTriggerResult] = useState(null);
    const [triggerError, setTriggerError] = useState(null);
    // polling state for async run-monthly
    const [pollingRunId, setPollingRunId] = useState(null);
    const pollingIntervalRef = useRef(null);

    // cleanup polling on unmount
    useEffect(() => () => { if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current); }, []);

    // polling effect — fires when a real (non-dry-run) job is triggered
    useEffect(() => {
        if (!pollingRunId) return;
        const poll = async () => {
            try {
                const data = await invoiceService.getRunDetail(pollingRunId);
                const run = data.run;
                if (!run) return;
                if (run.status === 'COMPLETED' || run.status === 'FAILED') {
                    clearInterval(pollingIntervalRef.current);
                    pollingIntervalRef.current = null;
                    setPollingRunId(null);
                    setTriggerLoading(false);
                    setTriggerResult({
                        dryRun: false,
                        run: {
                            counters: {
                                invoicesGenerated: run.invoicesGenerated,
                                invoicesSent: run.invoicesSent,
                                invoicesFailed: run.invoicesFailed,
                                invoicesSkipped: run.invoicesSkipped,
                            },
                        },
                        failed: run.status === 'FAILED',
                    });
                    if (run.status === 'FAILED') {
                        setTriggerError('Job terminato con errori. Controlla i dettagli nel Run History.');
                    }
                    await loadRuns();
                    setCoverageKey((k) => k + 1);
                } else {
                    // Still RUNNING — update live counters
                    setTriggerResult({
                        polling: true,
                        run: {
                            counters: {
                                invoicesGenerated: run.invoicesGenerated,
                                invoicesSent: run.invoicesSent,
                                invoicesFailed: run.invoicesFailed,
                                invoicesSkipped: run.invoicesSkipped,
                            },
                        },
                    });
                }
            } catch (e) {
                // transient error — don't stop polling
                console.warn('[polling] getRunDetail error:', e.message);
            }
        };
        poll();
        pollingIntervalRef.current = setInterval(poll, 3000);
        return () => clearInterval(pollingIntervalRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pollingRunId]);

    // tenants list for locale selector
    const [tenants, setTenants] = useState([]);
    const [tenantsLoading, setTenantsLoading] = useState(false);

    // manual trigger — retry
    const [retryDryRun, setRetryDryRun] = useState(false);
    const [retryLoading, setRetryLoading] = useState(false);
    const [retryResult, setRetryResult] = useState(null);
    const [retryError, setRetryError] = useState(null);

    // single invoice retry
    const [retryingId, setRetryingId] = useState(null); // invoiceId currently being retried
    const [retryFeedback, setRetryFeedback] = useState({}); // { [invoiceId]: 'ok' | 'error' | message }

    // billing health
    const [health, setHealth] = useState(null);
    const [healthLoading, setHealthLoading] = useState(false);
    const [healthError, setHealthError] = useState(null);
    const [healthHelpOpen, setHealthHelpOpen] = useState(false);

    // coverage help
    const [coverageHelpOpen, setCoverageHelpOpen] = useState(false);

    // cron kill-switches
    const [cronFlags, setCronFlags] = useState({ invoiceMonthlyCronEnabled: true, invoiceRetryCronEnabled: true });
    const [cronFlagsLoading, setCronFlagsLoading] = useState(false);
    const [cronFlagsSaving, setCronFlagsSaving] = useState(null); // 'monthly' | 'retry' | null

    // ── load on mount ──────────────────────────────────────────────────────────
    useEffect(() => {
        loadRuns();
        loadHealth();
        loadCronFlags();
        loadTenants();
    }, []);

    useEffect(() => {
        if (verifyMonth) loadVerify();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [verifyMonth]);

    // ── data fetchers ──────────────────────────────────────────────────────────

    async function loadRuns() {
        try {
            setRunsLoading(true);
            setRunsError(null);
            const data = await invoiceService.getInvoiceRuns(20);
            setRuns(data.runs || []);
        } catch (e) {
            setRunsError('Errore caricamento storico: ' + e.message);
        } finally {
            setRunsLoading(false);
        }
    }

    async function loadHealth() {
        try {
            setHealthLoading(true);
            setHealthError(null);
            const data = await invoiceService.getHealth();
            setHealth(data);
        } catch (e) {
            setHealthError(e.message);
        } finally {
            setHealthLoading(false);
        }
    }

    async function loadCronFlags() {
        try {
            setCronFlagsLoading(true);
            const data = await cronFlagsService.get();
            setCronFlags(data);
        } catch (e) {
            console.error('Errore caricamento cron flags:', e.message);
        } finally {
            setCronFlagsLoading(false);
        }
    }

    async function toggleCronFlag(flagKey) {
        const savingKey = flagKey === 'invoiceMonthlyCronEnabled' ? 'monthly' : 'retry';
        try {
            setCronFlagsSaving(savingKey);
            const newValue = !cronFlags[flagKey];
            const updated = await cronFlagsService.update({ [flagKey]: newValue });
            setCronFlags(updated);
        } catch (e) {
            console.error('Errore aggiornamento cron flag:', e.message);
        } finally {
            setCronFlagsSaving(null);
        }
    }

    async function loadTenants() {
        try {
            setTenantsLoading(true);
            const data = await tenantsService.getAll();
            setTenants(data);
        } catch (e) {
            console.error('Errore caricamento tenants:', e.message);
        } finally {
            setTenantsLoading(false);
        }
    }

    async function loadVerify() {
        try {
            setVerifyLoading(true);
            setVerifyError(null);
            setVerifyRun(null);
            setVerifyInvoices([]);

            const [runsData, invoicesData] = await Promise.all([
                invoiceService.getInvoiceRuns(100, verifyMonth),
                invoiceService.getInvoices({ yearMonth: verifyMonth, limit: 200 }),
            ]);

            // pick the most recent run for this month
            const monthRuns = (runsData.runs || []).filter((r) => r.yearMonth === verifyMonth);
            setVerifyRun(monthRuns[0] || null);
            setVerifyInvoices(invoicesData.invoices || []);
        } catch (e) {
            setVerifyError('Errore verifica mese: ' + e.message);
        } finally {
            setVerifyLoading(false);
        }
    }

    // ── actions ────────────────────────────────────────────────────────────────

    async function handleRunMonthly() {
        try {
            setTriggerLoading(true);
            setTriggerResult(null);
            setTriggerError(null);
            const result = await invoiceService.runMonthlyJob(
                triggerMonth || null,
                triggerDryRun,
                triggerTenantId || null,
            );
            if (!triggerDryRun && result.async && result.runId) {
                // Real run: server responded immediately with runId — start polling
                setPollingRunId(result.runId);
                // triggerLoading stays true until polling completes
            } else {
                // Dry-run: synchronous result
                setTriggerResult(result);
                await loadRuns();
                if (verifyMonth === triggerMonth) await loadVerify();
                setCoverageKey((k) => k + 1);
                setTriggerLoading(false);
            }
        } catch (e) {
            setTriggerError(e.response?.data?.message || e.message);
            setTriggerLoading(false);
        }
    }

    async function handleRunRetry() {
        try {
            setRetryLoading(true);
            setRetryResult(null);
            setRetryError(null);
            const result = await invoiceService.runRetryJob(retryDryRun);
            setRetryResult(result);
            await loadRuns();
            await loadVerify();
            setCoverageKey((k) => k + 1);
        } catch (e) {
            setRetryError(e.response?.data?.message || e.message);
        } finally {
            setRetryLoading(false);
        }
    }

    async function handleRetrySingle(invoiceId) {
        setRetryingId(invoiceId);
        setRetryFeedback((prev) => ({ ...prev, [invoiceId]: null }));
        try {
            await invoiceService.retrySingleInvoice(invoiceId);
            setRetryFeedback((prev) => ({ ...prev, [invoiceId]: 'ok' }));
            // Refresh tables
            await Promise.all([loadHealth(), loadVerify()]);
        } catch (e) {
            const msg = e.response?.data?.message || e.message || 'Errore retry';
            setRetryFeedback((prev) => ({ ...prev, [invoiceId]: msg }));
        } finally {
            setRetryingId(null);
        }
    }

    // ── derived data ───────────────────────────────────────────────────────────

    const errorLogEntries = (() => {
        if (!verifyRun?.errorLog) return [];
        try {
            return typeof verifyRun.errorLog === 'string'
                ? JSON.parse(verifyRun.errorLog)
                : verifyRun.errorLog;
        } catch {
            return [];
        }
    })();

    // ── render ─────────────────────────────────────────────────────────────────

    return (
        <div className="im-page">
            {/* ── Header ── */}
            <div className="im-header">
                <div>
                    <h1>
                        <FileText size={28} />
                        Monitoraggio Fatturazione
                    </h1>
                    <p>Controlla il job mensile e verifica le fatture per ogni percettore</p>
                </div>
                <button className="im-btn im-btn-ghost" onClick={loadRuns} disabled={runsLoading}>
                    <RefreshCw size={16} className={runsLoading ? 'spinning' : ''} />
                    Aggiorna
                </button>
            </div>

            {/* ── Cron Kill-switches ── */}
            <div className="im-section">
                <div className="im-section-header">
                    <h2><Settings size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />Controllo Job Automatici</h2>
                    <span className="im-section-sub">Attiva o disattiva i cron job senza necessità di deploy</span>
                </div>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', padding: '12px 0' }}>
                    {[
                        { key: 'invoiceMonthlyCronEnabled', label: 'Job mensile fatture', savingKey: 'monthly' },
                        { key: 'invoiceRetryCronEnabled', label: 'Job retry / quarantena', savingKey: 'retry' },
                    ].map(({ key, label, savingKey }) => {
                        const enabled = cronFlags[key];
                        const saving = cronFlagsSaving === savingKey;
                        return (
                            <div key={key} className="im-cron-flag-card" style={{
                                display: 'flex', alignItems: 'center', gap: 12,
                                background: enabled ? 'var(--color-success-bg, #f0fdf4)' : 'var(--color-error-bg, #fef2f2)',
                                border: `1px solid ${enabled ? '#86efac' : '#fca5a5'}`,
                                borderRadius: 8, padding: '10px 16px', minWidth: 220,
                            }}>
                                <span style={{ fontWeight: 600, fontSize: 14, flex: 1 }}>{label}</span>
                                <span style={{
                                    fontSize: 12, fontWeight: 700,
                                    color: enabled ? '#16a34a' : '#dc2626',
                                    minWidth: 56, textAlign: 'center',
                                }}>
                                    {cronFlagsLoading ? '...' : enabled ? 'ATTIVO' : 'DISATTIVO'}
                                </span>
                                <button
                                    className={`im-btn ${enabled ? 'im-btn-danger' : 'im-btn-success'}`}
                                    style={{ minWidth: 90, fontSize: 13 }}
                                    disabled={cronFlagsLoading || !!cronFlagsSaving}
                                    onClick={() => toggleCronFlag(key)}
                                >
                                    {saving ? <Loader size={14} className="spinning" /> : (enabled ? 'Disattiva' : 'Attiva')}
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* ── Salute Fatturazione ── */}
            <div className="im-section">
                <div className="im-section-header">
                    <h2>Salute Fatturazione</h2>
                    <span className="im-section-sub">
                        {health?.lastOkMonth ? `Ultimo mese OK: ${health.lastOkMonth}` : ''}
                    </span>
                    <button
                        className="cov-ctrl-btn"
                        style={{ marginLeft: 'auto' }}
                        onClick={() => setHealthHelpOpen((v) => !v)}
                        title={healthHelpOpen ? 'Chiudi guida' : 'Mostra guida agli stati'}
                        aria-pressed={healthHelpOpen}
                    >
                        <HelpCircle size={14} style={{ color: healthHelpOpen ? '#4c51bf' : undefined }} />
                    </button>
                    <button
                        className="cov-ctrl-btn"
                        onClick={loadHealth}
                        disabled={healthLoading}
                        title="Aggiorna"
                    >
                        <RefreshCw size={13} className={healthLoading ? 'spinning' : ''} />
                    </button>
                </div>

                {healthHelpOpen && (
                    <div className="im-help-box">
                        <div className="im-help-row">
                            <span className="im-help-dot hk-error-dot" />
                            <div>
                                <strong>Errori A-Cube</strong> — Fatture che A-Cube ha rifiutato o messo in quarantena
                                (<code>error</code>, <code>rejected</code>, <code>quarantena</code>).
                                Richiedono intervento: correggere i dati del percettore e usare <em>Avvia Retry</em>, oppure gestire manualmente su A-Cube.
                            </div>
                        </div>
                        <div className="im-help-row">
                            <span className="im-help-dot hk-warn-dot" />
                            <div>
                                <strong>In attesa / non inviate</strong> — Fatture generate nel DB ma senza conferma da A-Cube
                                (stato <code>pending</code> o non impostato). Normale subito dopo la generazione;
                                se persistono a lungo senza avanzare, l’invio potrebbe essere bloccato.
                            </div>
                        </div>
                        <div className="im-help-row">
                            <span className="im-help-dot hk-ok-dot" />
                            <div>
                                <strong>Inviate con successo</strong> — A-Cube ha accettato e processato la fattura
                                (<code>sent</code>, <code>delivered</code>, <code>accepted</code>). Flusso completato correttamente.
                            </div>
                        </div>
                        <div className="im-help-row">
                            <span className="im-help-dot hk-total-dot" />
                            <div>
                                <strong>Worker con pendenze</strong> — Lista dei percettori con almeno una fattura non in stato OK.
                                Clicca il numero di fattura per aprire il dettaglio e intervenire.
                            </div>
                        </div>
                    </div>
                )}

                {healthError && (
                    <div className="im-alert im-alert-error"><AlertCircle size={16} />{healthError}</div>
                )}

                {health && (
                    <>
                        {/* Semaforo KPI */}
                        <div className="im-health-kpi">
                            <div className={`im-health-kpi-item ${health.counts.error > 0 ? 'hk-error' : 'hk-ok'}`}>
                                <span className="hk-value">{health.counts.error}</span>
                                <span className="hk-label">Errori A-Cube</span>
                            </div>
                            <div className={`im-health-kpi-item ${health.counts.pending > 0 ? 'hk-warn' : 'hk-ok'}`}>
                                <span className="hk-value">{health.counts.pending}</span>
                                <span className="hk-label">In attesa / non inviate</span>
                            </div>
                            <div className="im-health-kpi-item hk-ok">
                                <span className="hk-value">{health.counts.ok}</span>
                                <span className="hk-label">Inviate con successo</span>
                            </div>
                            <div className="im-health-kpi-item hk-total">
                                <span className="hk-value">{health.counts.total}</span>
                                <span className="hk-label">Totale fatture</span>
                            </div>
                        </div>

                        {/* Worker con pendenze */}
                        {health.issues.length === 0 ? (
                            <div className="im-empty-small" style={{ marginTop: '1rem' }}>
                                <CheckCircle size={18} style={{ color: '#059669' }} />
                                <span style={{ color: '#059669' }}>Nessuna pendenza — tutto in ordine</span>
                            </div>
                        ) : (
                            <>
                                <h3 style={{ margin: '1.2rem 0 0.6rem', fontSize: '0.92rem', color: '#374151' }}>
                                    Worker con pendenze ({health.issues.length})
                                </h3>
                                <div className="im-table-wrapper">
                                    <table className="im-table">
                                        <thead>
                                            <tr>
                                                <th>Percettore</th>
                                                <th>Fattura</th>
                                                <th>Mese</th>
                                                <th>Stato A-Cube</th>
                                                <th className="hide-mobile">Errore</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {health.issues.map((inv) => {
                                                const name = [inv.workerPaymentProfile?.verified_first_name, inv.workerPaymentProfile?.verified_last_name].filter(Boolean).join(' ') || inv.workerPaymentProfile?.user?.email || '—';
                                                const isError = inv.acubeInvoiceStatus === 'error' || inv.acubeInvoiceStatus === 'rejected' || inv.acubeInvoiceStatus === 'quarantined' || inv.acubeInvoiceStatus === 'quarantena';
                                                const canRetry = !inv.acubeInvoiceId || inv.acubeInvoiceStatus === 'error' || inv.acubeInvoiceStatus === 'rejected';
                                                const isBusy = retryingId === inv.id;
                                                const feedback = retryFeedback[inv.id];
                                                return (
                                                    <tr key={inv.id} className={isError ? 'row-warn' : ''}>
                                                        <td>{name}<br /><small style={{ color: '#9ca3af' }}>{inv.workerPaymentProfile?.user?.email}</small></td>
                                                        <td>
                                                            <button className="cov-invoice-link" onClick={() => navigate(`/admin/invoices/${inv.id}`)}>
                                                                {inv.invoiceNumber}
                                                            </button>
                                                        </td>
                                                        <td><span className="im-month-pill">{inv.yearMonth}</span></td>
                                                        <td>
                                                            <span style={{ fontWeight: 600, fontSize: 12, color: isError ? '#dc2626' : '#d97706' }}>
                                                                {inv.acubeInvoiceStatus || 'pending'}
                                                            </span>
                                                        </td>
                                                        <td className="hide-mobile">
                                                            <small style={{ color: '#6b7280' }}>{inv.acubeInvoiceError || '—'}</small>
                                                        </td>
                                                        <td style={{ whiteSpace: 'nowrap' }}>
                                                            {canRetry && (
                                                                <button
                                                                    className="im-retry-btn"
                                                                    disabled={!!retryingId}
                                                                    onClick={() => handleRetrySingle(inv.id)}
                                                                    title="Ritenta invio a A-Cube"
                                                                >
                                                                    {isBusy ? <Loader size={12} className="spinning" /> : <RefreshCw size={12} />}
                                                                    {isBusy ? 'Invio…' : 'Retry'}
                                                                </button>
                                                            )}
                                                            {feedback === 'ok' && <span style={{ color: '#16a34a', fontSize: 12, marginLeft: 4 }}>✓</span>}
                                                            {feedback && feedback !== 'ok' && <span style={{ color: '#dc2626', fontSize: 11, marginLeft: 4 }} title={feedback}>✗</span>}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                    </>
                )}

                {!health && !healthLoading && !healthError && (
                    <div className="im-empty-small"><Clock size={20} /><span>Caricamento...</span></div>
                )}
            </div>

            {/* ── Azioni manuali ── */}
            <div className="im-section im-actions-row">
                {/* Job mensile */}
                <div className="im-action-card">
                    <h2>
                        <Play size={18} />
                        Avvia Job Mensile
                    </h2>
                    <p className="im-action-desc">
                        Genera le fatture per tutti i percettori con commissioni nel mese selezionato.
                        Seleziona un locale per limitare il job a quel tenant.
                    </p>
                    <div className="im-action-controls">
                        <div className="im-fields-row">
                            <div className="im-field">
                                <label>Mese (YYYY-MM)</label>
                                <input
                                    type="month"
                                    value={triggerMonth}
                                    onChange={(e) => setTriggerMonth(e.target.value)}
                                    disabled={triggerLoading}
                                />
                            </div>
                            <div className="im-field">
                                <label>
                                    <Building2 size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                                    Locale
                                </label>
                                <select
                                    value={triggerTenantId}
                                    onChange={(e) => setTriggerTenantId(e.target.value)}
                                    disabled={triggerLoading || tenantsLoading}
                                >
                                    <option value="">Tutti i locali</option>
                                    {tenants.map((t) => (
                                        <option key={t.id} value={t.id}>
                                            {t.company_name || t.id}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="im-field im-field-toggle">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={triggerDryRun}
                                    onChange={(e) => setTriggerDryRun(e.target.checked)}
                                    disabled={triggerLoading}
                                />
                                Dry-run (simula, non crea fatture)
                            </label>
                        </div>
                    </div>
                    <button
                        className={`im-btn im-btn-primary ${triggerDryRun ? 'im-btn-dryrun' : ''}`}
                        onClick={handleRunMonthly}
                        disabled={triggerLoading}
                    >
                        {triggerLoading ? (
                            <Loader size={16} className="spinning" />
                        ) : (
                            <Play size={16} />
                        )}
                        {pollingRunId ? 'In esecuzione...' : triggerDryRun ? 'Simula Job' : 'Avvia Job'}
                    </button>

                    {triggerError && (
                        <div className="im-alert im-alert-error">
                            <AlertCircle size={16} />
                            {triggerError}
                        </div>
                    )}
                    {triggerResult && (() => {
                        const c = triggerResult.run?.counters || triggerResult.run || {};
                        const isPolling = triggerResult.polling === true;
                        return (
                            <div className={`im-alert ${isPolling ? 'im-alert-info' :
                                triggerResult.failed ? 'im-alert-error' :
                                    triggerResult.dryRun ? 'im-alert-info' : 'im-alert-ok'
                                }`}>
                                {isPolling ? <Loader size={16} className="spinning" /> : <CheckCircle size={16} />}
                                {isPolling ? 'In esecuzione — ' : triggerResult.dryRun ? '[DRY-RUN] ' : 'Completato — '}
                                Generati: {c.invoicesGenerated ?? 0} &nbsp;|&nbsp;
                                Inviati: {c.invoicesSent ?? 0} &nbsp;|&nbsp;
                                Falliti: {c.invoicesFailed ?? 0} &nbsp;|&nbsp;
                                Saltati: {c.invoicesSkipped ?? 0}
                            </div>
                        );
                    })()}
                </div>

                {/* Retry */}
                <div className="im-action-card">
                    <h2>
                        <RefreshCw size={18} />
                        Retry Fatture in Errore
                    </h2>
                    <p className="im-action-desc">
                        Re-invia ad A-Cube le fatture <strong>non inviate</strong> o con stato <strong>error</strong>.
                        Per le fatture in <strong>quarantena</strong> (in attesa di verifica SDI) aggiorna lo stato senza re-inviare —
                        il dato verrà corretto automaticamente quando l'SDI emetterà l'esito.
                    </p>
                    <div className="im-action-controls">
                        <div className="im-field im-field-toggle">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={retryDryRun}
                                    onChange={(e) => setRetryDryRun(e.target.checked)}
                                    disabled={retryLoading}
                                />
                                Dry-run (simula, non modifica dati)
                            </label>
                        </div>
                    </div>
                    <button
                        className={`im-btn im-btn-secondary ${retryDryRun ? 'im-btn-dryrun' : ''}`}
                        onClick={handleRunRetry}
                        disabled={retryLoading}
                    >
                        {retryLoading ? (
                            <Loader size={16} className="spinning" />
                        ) : (
                            <RefreshCw size={16} />
                        )}
                        {retryDryRun ? 'Simula Retry' : 'Avvia Retry'}
                    </button>

                    {retryError && (
                        <div className="im-alert im-alert-error">
                            <AlertCircle size={16} />
                            {retryError}
                        </div>
                    )}
                    {retryResult && (
                        <>
                            <div className={`im-alert ${retryResult.dryRun ? 'im-alert-info' : 'im-alert-ok'}`}>
                                <CheckCircle size={16} />
                                <span>
                                    {retryResult.dryRun ? '[DRY-RUN] ' : ''}Retry completato
                                    {retryResult.result && (
                                        <span style={{ marginLeft: 8, fontWeight: 400 }}>
                                            &mdash; Fatture da ritentare: <strong>{retryResult.result.retried ?? 0}</strong>
                                            {(retryResult.result.retriedFailed ?? 0) > 0 && <> · Fallite: <strong style={{ color: '#dc2626' }}>{retryResult.result.retriedFailed}</strong></>}
                                            {(retryResult.result.polled ?? 0) > 0 && <> · Quarantena poll: <strong>{retryResult.result.polled}</strong></>}
                                            {retryResult.result.retried === 0 && retryResult.result.polled === 0 && <> &mdash; nessuna fattura da ritentare</>}
                                        </span>
                                    )}
                                </span>
                            </div>

                            {/* Report dry-run: tabella candidati */}
                            {retryResult.dryRun && retryResult.result?.candidates?.length > 0 && (
                                <div style={{ marginTop: '0.75rem' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                                        <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>
                                            Fatture che verrebbero ritentate ({retryResult.result.candidates.length})
                                        </span>
                                        <button
                                            className="cov-ctrl-btn cov-ctrl-export"
                                            onClick={() => exportRetryCandidatesCsv(retryResult.result.candidates)}
                                            title="Scarica report simulazione"
                                        >
                                            <Download size={13} /> Scarica report
                                        </button>
                                    </div>
                                    <div className="im-table-wrapper">
                                        <table className="im-table">
                                            <thead>
                                                <tr>
                                                    <th>Percettore</th>
                                                    <th>Fattura</th>
                                                    <th>Mese</th>
                                                    <th>Motivo</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {retryResult.result.candidates.map((inv) => {
                                                    const name = [inv.workerPaymentProfile?.verified_first_name, inv.workerPaymentProfile?.verified_last_name].filter(Boolean).join(' ') || inv.workerPaymentProfile?.user?.email || '—';
                                                    return (
                                                        <tr key={inv.id} className="row-warn">
                                                            <td>
                                                                {name}
                                                                <br /><small style={{ color: '#9ca3af' }}>{inv.workerPaymentProfile?.user?.email}</small>
                                                            </td>
                                                            <td>
                                                                <button className="cov-invoice-link" onClick={() => navigate(`/admin/invoices/${inv.id}`)}>
                                                                    {inv.invoiceNumber}
                                                                </button>
                                                            </td>
                                                            <td><span className="im-month-pill">{inv.yearMonth}</span></td>
                                                            <td><small style={{ color: '#6b7280' }}>{inv.reason}</small></td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* ── Verifica per mese ── */}
            <div className="im-section">
                <div className="im-section-header">
                    <h2>Verifica per Mese</h2>
                    <div className="im-field im-field-inline">
                        <label>Mese:</label>
                        <input
                            type="month"
                            value={verifyMonth}
                            onChange={(e) => setVerifyMonth(e.target.value)}
                        />
                    </div>
                </div>

                {verifyError && (
                    <div className="im-alert im-alert-error">
                        <AlertCircle size={16} />
                        {verifyError}
                    </div>
                )}

                {verifyLoading ? (
                    <div className="im-loading-row">
                        <Loader size={20} className="spinning" />
                        Caricamento...
                    </div>
                ) : (
                    <>
                        {/* Run per questo mese */}
                        {verifyRun ? (
                            <div className="im-verify-run-row">
                                <RunStatusBadge status={verifyRun.status} />
                                <span>
                                    Job del {formatDate(verifyRun.startedAt)} — Valutati:{' '}
                                    <strong>{verifyRun.totalWorkers}</strong> &nbsp;|&nbsp; Generati:{' '}
                                    <strong>{verifyRun.invoicesGenerated}</strong> &nbsp;|&nbsp; Inviati:{' '}
                                    <strong>{verifyRun.invoicesSent}</strong> &nbsp;|&nbsp; Falliti:{' '}
                                    <strong
                                        className={(verifyRun.invoicesFailed ?? 0) > 0 ? 'text-err' : ''}
                                    >
                                        {verifyRun.invoicesFailed}
                                    </strong>
                                    &nbsp;|&nbsp; Saltati: <strong>{verifyRun.invoicesSkipped}</strong>
                                </span>
                            </div>
                        ) : (
                            <div className="im-alert im-alert-warn">
                                <AlertTriangle size={16} />
                                Nessun job eseguito per il mese <strong>{verifyMonth}</strong>.
                                Usa "Avvia Job Mensile" per generare le fatture.
                            </div>
                        )}

                        {/* Fatture mancanti (errorLog) */}
                        {errorLogEntries.length > 0 && (
                            <div className="im-missing-block">
                                <button
                                    className="im-collapsible"
                                    onClick={() => setExpandedErrors((v) => !v)}
                                >
                                    {expandedErrors ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                    <AlertTriangle size={16} className="icon-warn" />
                                    <strong>{errorLogEntries.length}</strong>&nbsp;percettore
                                    {errorLogEntries.length > 1 ? 'i' : ''} con fattura mancante
                                </button>
                                {expandedErrors && (
                                    <table className="im-table im-table-missing">
                                        <thead>
                                            <tr>
                                                <th>Percettore</th>
                                                <th>Errore</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {errorLogEntries.map((entry, i) => (
                                                <tr key={i}>
                                                    <td>
                                                        <div className="im-worker-cell">
                                                            <span>{entry.workerName || '—'}</span>
                                                            <small>{entry.workerEmail || entry.workerPaymentProfileId}</small>
                                                        </div>
                                                    </td>
                                                    <td className="im-error-msg">{entry.error}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        )}

                        {/* Fatture generate per questo mese */}
                        {verifyInvoices.length > 0 ? (
                            <div className="im-verify-invoices">
                                <h3>
                                    Fatture emesse ({verifyInvoices.length})
                                </h3>
                                <table className="im-table">
                                    <thead>
                                        <tr>
                                            <SortTh label="Percettore" sortKey="percettore" current={verifySort.key} dir={verifySort.dir} onSort={handleVerifySort} />
                                            <SortTh label="Numero" sortKey="invoiceNumber" current={verifySort.key} dir={verifySort.dir} onSort={handleVerifySort} />
                                            <SortTh label="Importo" sortKey="totalFeeTotal" current={verifySort.key} dir={verifySort.dir} onSort={handleVerifySort} className="text-right" />
                                            <SortTh label="Stato A-Cube" sortKey="acubeInvoiceStatus" current={verifySort.key} dir={verifySort.dir} onSort={handleVerifySort} />
                                            <SortTh label="Data generazione" sortKey="generatedAt" current={verifySort.key} dir={verifySort.dir} onSort={handleVerifySort} className="hide-mobile" />
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedVerifyInvoices.map((inv) => {
                                            const canRetry = !inv.acubeInvoiceId || inv.acubeInvoiceStatus === 'error' || inv.acubeInvoiceStatus === 'rejected';
                                            const isBusy = retryingId === inv.id;
                                            const feedback = retryFeedback[inv.id];
                                            return (
                                                <tr key={inv.id}>
                                                    <td>
                                                        <div className="im-worker-cell">
                                                            <span>
                                                                {inv.workerPaymentProfile?.verified_first_name || ''}{' '}
                                                                {inv.workerPaymentProfile?.verified_last_name || ''}
                                                            </span>
                                                            <small>{inv.workerPaymentProfile?.user?.email || '—'}</small>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <strong>{inv.invoiceNumber}</strong>
                                                    </td>
                                                    <td className="text-right">{formatCurrency(inv.totalFeeTotal)}</td>
                                                    <td>
                                                        <AcubeBadge status={inv.acubeInvoiceStatus} />
                                                    </td>
                                                    <td className="hide-mobile im-date-small">
                                                        {formatDate(inv.generatedAt)}
                                                    </td>
                                                    <td style={{ whiteSpace: 'nowrap' }}>
                                                        {canRetry && (
                                                            <button
                                                                className="im-retry-btn"
                                                                disabled={!!retryingId}
                                                                onClick={() => handleRetrySingle(inv.id)}
                                                                title="Ritenta invio a A-Cube"
                                                            >
                                                                {isBusy ? <Loader size={12} className="spinning" /> : <RefreshCw size={12} />}
                                                                {isBusy ? 'Invio…' : 'Retry'}
                                                            </button>
                                                        )}
                                                        {feedback === 'ok' && <span style={{ color: '#16a34a', fontSize: 12, marginLeft: 4 }}>✓</span>}
                                                        {feedback && feedback !== 'ok' && <span style={{ color: '#dc2626', fontSize: 11, marginLeft: 4 }} title={feedback}>✗</span>}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            verifyRun && (
                                <div className="im-empty-small" style={{ marginTop: '1rem' }}>
                                    <FileText size={20} />
                                    <span>Nessuna fattura trovata per {verifyMonth}</span>
                                </div>
                            )
                        )}
                    </>
                )}
            </div>

            {/* ── Copertura per Tenant ── */}
            <div className="im-section">
                <div className="im-section-header">
                    <h2 style={{ marginBottom: 0 }}>Copertura per Locale — {verifyMonth}</h2>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 'auto', minWidth: 0 }}>
                        <span className="im-section-sub" style={{ whiteSpace: 'nowrap' }}>
                            Chi ha ricevuto mance e chi è stato fatturato
                        </span>
                        <button
                            className="cov-ctrl-btn"
                            style={{ flexShrink: 0 }}
                            onClick={() => setCoverageHelpOpen((v) => !v)}
                            title={coverageHelpOpen ? 'Chiudi guida' : 'Mostra guida alla copertura'}
                            aria-pressed={coverageHelpOpen}
                        >
                            <HelpCircle size={14} style={{ color: coverageHelpOpen ? '#4c51bf' : undefined }} />
                        </button>
                    </div>
                </div>
                <CoverageSection
                    verifyMonth={verifyMonth}
                    refreshKey={coverageKey}
                    coverageHelpOpen={coverageHelpOpen}
                />
            </div>

            {/* ── Storico Job ── */}
            <div className="im-section">
                <div className="im-section-header">
                    <h2>Storico Job</h2>
                    <span className="im-section-sub">ultimi {runs.length}</span>
                    {runs.length > 0 && (
                        <button
                            className="cov-ctrl-btn cov-ctrl-export"
                            style={{ marginLeft: 'auto' }}
                            onClick={() => exportRunsCsv(runs)}
                            title="Esporta storico job in CSV"
                        >
                            <Download size={14} /> Esporta CSV
                        </button>
                    )}
                </div>

                {runsError && (
                    <div className="im-alert im-alert-error">
                        <AlertCircle size={16} />
                        {runsError}
                    </div>
                )}

                {runs.length === 0 && !runsLoading ? (
                    <div className="im-empty-small">
                        <Clock size={20} />
                        <span>Nessun job eseguito</span>
                    </div>
                ) : (
                    <div className="im-table-wrapper">
                        <table className="im-table">
                            <thead>
                                <tr>
                                    <th style={{ width: 28 }}></th>
                                    <SortTh label="Mese" sortKey="yearMonth" current={runsSort.key} dir={runsSort.dir} onSort={handleRunsSort} />
                                    <SortTh label="Stato" sortKey="status" current={runsSort.key} dir={runsSort.dir} onSort={handleRunsSort} />
                                    <SortTh label="Valutati" sortKey="totalWorkers" current={runsSort.key} dir={runsSort.dir} onSort={handleRunsSort} className="text-center" />
                                    <SortTh label="Generati" sortKey="invoicesGenerated" current={runsSort.key} dir={runsSort.dir} onSort={handleRunsSort} className="text-center" />
                                    <SortTh label="Inviati" sortKey="invoicesSent" current={runsSort.key} dir={runsSort.dir} onSort={handleRunsSort} className="text-center" />
                                    <SortTh label="Falliti" sortKey="invoicesFailed" current={runsSort.key} dir={runsSort.dir} onSort={handleRunsSort} className="text-center im-col-fail" />
                                    <SortTh label="Saltati" sortKey="invoicesSkipped" current={runsSort.key} dir={runsSort.dir} onSort={handleRunsSort} className="text-center hide-mobile" />
                                    <SortTh label="Avviato il" sortKey="startedAt" current={runsSort.key} dir={runsSort.dir} onSort={handleRunsSort} className="hide-mobile" />
                                    <SortTh label="Da" sortKey="triggeredBy" current={runsSort.key} dir={runsSort.dir} onSort={handleRunsSort} className="hide-mobile" />
                                </tr>
                            </thead>
                            <tbody>
                                {sortedRuns.map((run) => (
                                    <RunRow key={run.id} run={run} navigate={navigate} />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

// ─── CoverageSection ─────────────────────────────────────────────────────────

// ─── RunRow — riga storico job con expand dettaglio fatture ───────────────────
function RunRow({ run, navigate }) {
    const [open, setOpen] = useState(false);
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const colSpan = 10;

    async function toggle() {
        if (open) { setOpen(false); return; }
        setOpen(true);
        if (detail) return; // già caricato
        try {
            setLoading(true);
            setError(null);
            const data = await invoiceService.getRunDetail(run.id);
            setDetail(data);
        } catch (e) {
            setError(e.response?.data?.message || e.message);
        } finally {
            setLoading(false);
        }
    }

    const hasGenerated = (run.invoicesGenerated ?? 0) > 0;

    return (
        <>
            <tr
                className={`${(run.invoicesFailed ?? 0) > 0 ? 'row-warn' : ''} ${hasGenerated ? 'run-row-clickable' : ''}`}
                onClick={hasGenerated ? toggle : undefined}
                style={hasGenerated ? { cursor: 'pointer' } : {}}
                title={hasGenerated ? 'Clicca per vedere le fatture generate' : ''}
            >
                <td style={{ textAlign: 'center', color: '#6b7280', width: 28 }}>
                    {hasGenerated
                        ? (open ? <ChevronDown size={14} /> : <ChevronRight size={14} />)
                        : null}
                </td>
                <td><span className="im-month-pill">{run.yearMonth || '—'}</span></td>
                <td><RunStatusBadge status={run.status} /></td>
                <td className="text-center">{run.totalWorkers ?? 0}</td>
                <td className="text-center">{run.invoicesGenerated ?? 0}</td>
                <td className="text-center">{run.invoicesSent ?? 0}</td>
                <td className={`text-center im-col-fail ${(run.invoicesFailed ?? 0) > 0 ? 'text-err' : ''}`}>
                    <strong>{run.invoicesFailed ?? 0}</strong>
                </td>
                <td className="text-center hide-mobile">{run.invoicesSkipped ?? 0}</td>
                <td className="hide-mobile im-date-small">{formatDate(run.startedAt)}</td>
                <td className="hide-mobile"><small>{run.triggeredBy === 'job' ? 'Schedulato' : run.triggeredBy?.startsWith('admin:') ? 'Admin' : run.triggeredBy || '—'}</small></td>
            </tr>
            {open && (
                <tr>
                    <td colSpan={colSpan} style={{ padding: 0, background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                        <div style={{ padding: '12px 20px' }}>
                            {loading && <span style={{ color: '#6b7280', fontSize: 13 }}>Caricamento...</span>}
                            {error && <span style={{ color: '#dc2626', fontSize: 13 }}>{error}</span>}
                            {detail && detail.invoices.length === 0 && (
                                <span style={{ color: '#6b7280', fontSize: 13 }}>Nessuna fattura trovata per questo run.</span>
                            )}
                            {detail && detail.invoices.length > 0 && (
                                <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ color: '#6b7280', textAlign: 'left' }}>
                                            <th style={{ padding: '4px 8px', fontWeight: 600 }}>Fattura</th>
                                            <th style={{ padding: '4px 8px', fontWeight: 600 }}>Percettore</th>
                                            <th style={{ padding: '4px 8px', fontWeight: 600, textAlign: 'right' }}>Lordo</th>
                                            <th style={{ padding: '4px 8px', fontWeight: 600, textAlign: 'right' }}>Comm.</th>
                                            <th style={{ padding: '4px 8px', fontWeight: 600, textAlign: 'center' }}>Trans.</th>
                                            <th style={{ padding: '4px 8px', fontWeight: 600, textAlign: 'center' }}>Stato A-Cube</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {detail.invoices.map((inv) => {
                                            const name = [inv.workerPaymentProfile?.verified_first_name, inv.workerPaymentProfile?.verified_last_name].filter(Boolean).join(' ') || inv.workerPaymentProfile?.user?.email || '—';
                                            const status = inv.acubeInvoiceStatus || 'pending';
                                            const statusColor = status === 'sent' || status === 'delivered' ? '#059669' : status === 'error' ? '#dc2626' : '#d97706';
                                            return (
                                                <tr key={inv.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                                                    <td style={{ padding: '5px 8px' }}>
                                                        <button
                                                            className="cov-invoice-link"
                                                            onClick={(e) => { e.stopPropagation(); navigate(`/admin/invoices/${inv.id}`); }}
                                                        >
                                                            {inv.invoiceNumber}
                                                        </button>
                                                    </td>
                                                    <td style={{ padding: '5px 8px' }}>{name}</td>
                                                    <td style={{ padding: '5px 8px', textAlign: 'right' }}>{((inv.totalGross || 0) / 100).toFixed(2)} €</td>
                                                    <td style={{ padding: '5px 8px', textAlign: 'right' }}>{((inv.totalFeeTotal || 0) / 100).toFixed(2)} €</td>
                                                    <td style={{ padding: '5px 8px', textAlign: 'center' }}>{inv.transactionCount ?? '—'}</td>
                                                    <td style={{ padding: '5px 8px', textAlign: 'center', color: statusColor, fontWeight: 600, fontSize: 12 }}>
                                                        {status}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
}

const WORKER_STATUS_CFG = {
    invoiced: { label: 'Fatturata', cls: 'cov-invoiced', icon: CheckCircle },
    pending_invoice: { label: 'Fattura in attesa', cls: 'cov-pending-inv', icon: Clock },
    not_invoiced: { label: 'Non fatturato', cls: 'cov-not-invoiced', icon: AlertTriangle },
    error: { label: 'Errore A-Cube', cls: 'cov-error', icon: XCircle },
    no_profile: { label: 'Profilo mancante', cls: 'cov-no-profile', icon: AlertCircle },
};

function WorkerStatusBadge({ status }) {
    const cfg = WORKER_STATUS_CFG[status] || { label: status, cls: 'cov-no-profile', icon: AlertCircle };
    const Icon = cfg.icon;
    return (
        <span className={`cov-status-badge ${cfg.cls}`}>
            <Icon size={13} />
            {cfg.label}
        </span>
    );
}

const STATUS_FILTERS = [
    { key: 'all', label: 'Tutti' },
    { key: 'problems', label: 'Solo problemi' },
    { key: 'error', label: 'Errori A-Cube' },
    { key: 'not_invoiced', label: 'Non fatturati' },
    { key: 'no_profile', label: 'Profilo mancante' },
    { key: 'invoiced', label: 'Fatturati' },
];

function CoverageSection({ verifyMonth, refreshKey, coverageHelpOpen }) {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [expanded, setExpanded] = useState({});
    const [statusFilter, setStatusFilter] = useState('all');
    const [tenantSearch, setTenantSearch] = useState('');

    useEffect(() => {
        if (verifyMonth) load(verifyMonth);
    }, [verifyMonth, refreshKey]);

    async function load(ym) {
        try {
            setLoading(true);
            setError(null);
            const result = await invoiceService.getCoverage(ym);
            setData(result);
            // Auto-expand tenants with problems
            const autoExpand = {};
            (result.tenants || []).forEach((t) => {
                if (t.missingCount > 0) autoExpand[t.tenantId] = true;
            });
            setExpanded(autoExpand);
        } catch (e) {
            setError('Errore caricamento copertura: ' + (e.response?.data?.message || e.message));
        } finally {
            setLoading(false);
        }
    }

    function toggleTenant(id) {
        setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
    }

    function expandAll() {
        const all = {};
        visibleTenants.forEach((t) => { all[t.tenantId] = true; });
        setExpanded(all);
    }
    function collapseAll() { setExpanded({}); }

    // Apply status filter + tenant search
    const matchesStatusFilter = (w) => {
        if (statusFilter === 'all') return true;
        if (statusFilter === 'problems') return ['error', 'not_invoiced', 'no_profile'].includes(w.status);
        return w.status === statusFilter;
    };

    const visibleTenants = (data?.tenants || []).map((t) => ({
        ...t,
        workers: t.workers.filter(matchesStatusFilter),
    })).filter((t) =>
        t.workers.length > 0 &&
        (!tenantSearch || t.tenantName.toLowerCase().includes(tenantSearch.toLowerCase()))
    );

    if (loading) {
        return (
            <div className="im-loading-row" style={{ padding: '2rem' }}>
                <Loader size={20} className="spinning" />
                Analisi copertura in corso...
            </div>
        );
    }

    if (error) {
        return (
            <div className="im-alert im-alert-error">
                <AlertCircle size={16} /> {error}
            </div>
        );
    }

    if (!data) return null;

    const { summary } = data;
    const tenants = visibleTenants;

    return (
        <div>
            {coverageHelpOpen && (
                <div className="im-help-box" style={{ marginBottom: '1rem' }}>
                    <div className="im-help-row">
                        <span className="im-help-dot hk-ok-dot" />
                        <span>
                            Questa sezione mostra, per il mese selezionato, i trasferimenti Stripe dei worker raggruppati per locale.
                            Un worker può comparire in più locali nello stesso mese se ha ricevuto mance in venue diverse.
                        </span>
                    </div>
                    <div className="im-help-row">
                        <span className="im-help-dot hk-warn-dot" />
                        <span>
                            Lo stato “già fatturato” non dipende dal singolo locale: fa riferimento alla fattura mensile del worker
                            ({' '}workerPaymentProfile + mese), quindi la stessa fattura può coprire transfer di venue diverse.
                        </span>
                    </div>
                    <div className="im-help-row">
                        <span className="im-help-dot hk-total-dot" />
                        <span>
                            Il mese di competenza è calcolato sul timestamp Stripe del transfer (<code>transferOccurredAtUtc</code>),
                            con fallback al dato tecnico <code>createdAt</code> solo per record legacy.
                        </span>
                    </div>
                </div>
            )}

            {/* ── Summary cards ── */}
            <div className="cov-summary-grid">
                <div className="cov-card cov-card-neutral">
                    <span className="cov-card-value">{summary.totalTenants}</span>
                    <span className="cov-card-label">Locali attivi</span>
                </div>
                <div className="cov-card cov-card-neutral">
                    <span className="cov-card-value">{summary.totalWorkers}</span>
                    <span className="cov-card-label">Worker con mance</span>
                </div>
                <div className={`cov-card ${summary.coveredWorkers === summary.totalWorkers ? 'cov-card-ok' : 'cov-card-warn'}`}>
                    <span className="cov-card-value">{summary.coveredWorkers}/{summary.totalWorkers}</span>
                    <span className="cov-card-label">Worker fatturati</span>
                </div>
                <div className={`cov-card ${summary.missingWorkers > 0 ? 'cov-card-err' : 'cov-card-ok'}`}>
                    <span className="cov-card-value">{summary.missingWorkers}</span>
                    <span className="cov-card-label">Worker senza fattura</span>
                </div>
                <div className="cov-card cov-card-neutral">
                    <span className="cov-card-value">{formatCurrency(summary.totalInvoiced)}</span>
                    <span className="cov-card-label">Totale fatturato</span>
                </div>
                {summary.totalPending > 0 && (
                    <div className="cov-card cov-card-warn">
                        <span className="cov-card-value">{formatCurrency(summary.totalPending)}</span>
                        <span className="cov-card-label">Ancora da fatturare</span>
                    </div>
                )}
            </div>

            {/* ── Controls ── */}
            <div className="cov-toolbar">
                {/* Search */}
                <div className="cov-search-wrap">
                    <Search size={14} className="cov-search-icon" />
                    <input
                        className="cov-search-input"
                        type="text"
                        placeholder="Cerca locale..."
                        value={tenantSearch}
                        onChange={(e) => setTenantSearch(e.target.value)}
                    />
                </div>

                {/* Status filter pills */}
                <div className="cov-filter-pills">
                    {STATUS_FILTERS.map((f) => (
                        <button
                            key={f.key}
                            className={`cov-filter-pill ${statusFilter === f.key ? 'cov-filter-active' : ''}`}
                            onClick={() => setStatusFilter(f.key)}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>

                {/* Expand/collapse + export */}
                <div className="cov-controls">
                    <button className="cov-ctrl-btn" onClick={expandAll}>
                        <ChevronDown size={14} /> Espandi
                    </button>
                    <button className="cov-ctrl-btn" onClick={collapseAll}>
                        <ChevronRight size={14} /> Comprimi
                    </button>
                    <button
                        className="cov-ctrl-btn cov-ctrl-export"
                        onClick={() => exportCoverageCsv(data)}
                        title="Esporta dati copertura in CSV (apribile con Excel)"
                    >
                        <Download size={14} /> Esporta CSV
                    </button>
                </div>
            </div>

            {tenants.length === 0 && (
                <div className="im-empty-small">
                    <FileText size={20} />
                    <span>
                        {statusFilter !== 'all' || tenantSearch
                            ? 'Nessun risultato per i filtri selezionati'
                            : `Nessun trasferimento trovato per ${data.yearMonth}`}
                    </span>
                </div>
            )}

            {/* ── Tenant accordions ── */}
            <div className="cov-tenant-list">
                {tenants.map((tenant) => {
                    const isOpen = !!expanded[tenant.tenantId];
                    const hasProblems = tenant.missingCount > 0;
                    return (
                        <div key={tenant.tenantId} className={`cov-tenant ${hasProblems ? 'cov-tenant-warn' : ''}`}>
                            {/* Tenant header */}
                            <button
                                className="cov-tenant-header"
                                onClick={() => toggleTenant(tenant.tenantId)}
                            >
                                <span className="cov-tenant-chevron">
                                    {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                </span>
                                <Building2 size={16} className="cov-tenant-icon" />
                                <span className="cov-tenant-name">{tenant.tenantName}</span>
                                <span className="cov-tenant-venues">
                                    {tenant.venues.slice(0, 2).join(', ')}
                                    {tenant.venues.length > 2 ? ` +${tenant.venues.length - 2}` : ''}
                                </span>

                                <div className="cov-tenant-stats">
                                    {/* Worker coverage pill */}
                                    <span className={`cov-pill ${hasProblems ? 'cov-pill-warn' : 'cov-pill-ok'}`}>
                                        <Users size={12} />
                                        {tenant.coveredCount}/{tenant.workerCount} worker
                                    </span>
                                    {/* Totale fatturato */}
                                    {tenant.totalInvoiced > 0 && (
                                        <span className="cov-pill cov-pill-neutral">
                                            ✓ {formatCurrency(tenant.totalInvoiced)}
                                        </span>
                                    )}
                                    {/* Pendenti */}
                                    {tenant.totalPending > 0 && (
                                        <span className="cov-pill cov-pill-warn">
                                            ⚠ {formatCurrency(tenant.totalPending)} da fatturare
                                        </span>
                                    )}
                                    {/* Badge problemi */}
                                    {hasProblems && (
                                        <span className="cov-pill cov-pill-err">
                                            {tenant.missingCount} mancante{tenant.missingCount > 1 ? 'i' : ''}
                                        </span>
                                    )}
                                </div>
                            </button>

                            {/* Worker table */}
                            {isOpen && (
                                <div className="cov-worker-table-wrap">
                                    <table className="im-table cov-worker-table">
                                        <thead>
                                            <tr>
                                                <th>Worker</th>
                                                <th>Stato</th>
                                                <th>Fattura</th>
                                                <th className="text-right">Comm. fatturate</th>
                                                <th className="text-right">Mance lorde</th>
                                                <th className="text-right hide-mobile">Da fatturare</th>
                                                <th className="hide-mobile">A-Cube</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {tenant.workers.map((w, i) => (
                                                <tr
                                                    key={w.workerPaymentProfileId || i}
                                                    className={
                                                        w.status === 'error' ? 'cov-row-error'
                                                            : w.status === 'not_invoiced' || w.status === 'no_profile' ? 'cov-row-missing'
                                                                : ''
                                                    }
                                                >
                                                    <td>
                                                        <div className="im-worker-cell">
                                                            <span>{w.workerName || <em>Nome non disponibile</em>}</span>
                                                            <small>{w.workerEmail || '—'}</small>
                                                            {!w.hasTaxId && w.workerPaymentProfileId && (
                                                                <small className="cov-warn-text">⚠ CF mancante</small>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <WorkerStatusBadge status={w.status} />
                                                    </td>
                                                    <td>
                                                        {w.invoice ? (
                                                            <button
                                                                className="cov-invoice-link"
                                                                onClick={() => navigate(`/admin/invoices/${w.invoice.id}`)}
                                                                title="Vai al dettaglio fattura"
                                                            >
                                                                {w.invoice.invoiceNumber}
                                                            </button>
                                                        ) : (
                                                            <span className="cov-no-invoice">—</span>
                                                        )}
                                                        {w.invoice?.acubeInvoiceError && (
                                                            <div className="im-error-msg" style={{ marginTop: '0.2rem' }}>
                                                                {w.invoice.acubeInvoiceError.substring(0, 80)}…
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="text-right">
                                                        {w.invoice ? (
                                                            <strong>{formatCurrency(w.feeTotalInvoiced)}</strong>
                                                        ) : '—'}
                                                    </td>
                                                    <td className="text-right">
                                                        {formatCurrency(w.amountGross)}
                                                        <div><small className="cov-dimmed">{w.transferCount} trans.</small></div>
                                                    </td>
                                                    <td className="text-right hide-mobile">
                                                        {w.feeTotalPending > 0 ? (
                                                            <span className="cov-pending-amount">
                                                                {formatCurrency(w.feeTotalPending)}
                                                            </span>
                                                        ) : '—'}
                                                    </td>
                                                    <td className="hide-mobile">
                                                        {w.invoice?.acubeInvoiceStatus ? (
                                                            <AcubeBadge status={w.invoice.acubeInvoiceStatus} />
                                                        ) : '—'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        {/* Tenant totals row */}
                                        <tfoot>
                                            <tr className="cov-totals-row">
                                                <td colSpan={3}><strong>Totale {tenant.tenantName}</strong></td>
                                                <td className="text-right">
                                                    <strong>{formatCurrency(tenant.totalInvoiced)}</strong>
                                                </td>
                                                <td className="text-right">
                                                    <strong>{formatCurrency(tenant.workers.reduce((s, w) => s + w.amountGross, 0))}</strong>
                                                </td>
                                                <td className="text-right hide-mobile">
                                                    {tenant.totalPending > 0 ? (
                                                        <strong className="cov-pending-amount">{formatCurrency(tenant.totalPending)}</strong>
                                                    ) : '—'}
                                                </td>
                                                <td className="hide-mobile" />
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default InvoiceMonitoring;
